/**
 * A RECUSA POR TETO DO PLANO, LIDA UMA VEZ.
 *
 * O servidor recusa com um CÓDIGO e, ao lado dele, os dois números que a pessoa
 * precisa de ver: quanto o plano dá e quanto já está em uso. Antes disto cada
 * porta lia o `payload` à sua maneira — e as que não liam nada mostravam a frase
 * "o seu plano não inclui mais um canal" sem dizer quantos, e sem oferecer o que
 * fazer a seguir.
 *
 * ── POR QUE MORA EM `shared/` E NÃO NA COBRANÇA ───────────────────────────
 *
 * Quem TROPEÇA no teto não é a tela de cobrança: é a de canais, a de equipe, a
 * caixa de entrada e toda porta de upload, que são features diferentes e não
 * podem importar a da cobrança (`no-cross-feature`). Isto aqui não sabe desenhar nada — é só a leitura do
 * envelope, e é por isso que pode ser partilhada.
 *
 * Números AUSENTES lêem-se zero, e zero é o sinal de "não sei": um servidor mais
 * velho recusa sem os mandar, e a tela que os desenha esconde a linha em vez de
 * escrever "0 de 0 canais em uso".
 */

/**
 * Os quatro tetos do plano, e todos BLOQUEIAM. Contatos e armazenamento só eram
 * medidos; agora o servidor recusa — o contato acima do teto fica retido, e o
 * upload da equipe com o armazenamento cheio não entra.
 */
export type LimitCode = "CHANNEL_LIMIT" | "USER_LIMIT" | "CONTACT_LIMIT" | "STORAGE_LIMIT";

export interface LimitRefusal {
	readonly code: LimitCode;
	/** Quantos o plano dá. Zero = o servidor não disse. */
	readonly limit: number;
	/** Quantos já estão em uso. Zero = o servidor não disse. */
	readonly used: number;
}

const LIMIT_CODES: readonly string[] = [
	"CHANNEL_LIMIT",
	"USER_LIMIT",
	"CONTACT_LIMIT",
	"STORAGE_LIMIT",
];

/**
 * Um número do fio, ou zero — nunca `NaN` a caminho de uma frase.
 *
 * SEM truncar: o armazenamento vem em GB e o uso é fracionário — 0,4 GB truncado
 * vira zero, que aqui quer dizer "não sei". Quem desenha arredonda (`Math.ceil`).
 */
function amount(value: unknown): number {
	return typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 0;
}

/**
 * A recusa, quando é uma recusa por teto — e `null` para todo o resto.
 *
 * Lida do `payload` sem depender da classe `HttpError`: quem chama está num
 * `catch`, onde o que existe é `unknown`.
 */
export function readLimitRefusal(e: unknown): LimitRefusal | null {
	const payload = (e as { payload?: { error?: unknown; limit?: unknown; used?: unknown } } | null)
		?.payload;
	const code = payload?.error;
	if (typeof code !== "string" || !LIMIT_CODES.includes(code)) return null;
	return { code: code as LimitCode, limit: amount(payload?.limit), used: amount(payload?.used) };
}
